import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Avatar,
  Alert,
  Snackbar,
  CircularProgress,
  Tooltip,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Event,
  Image,
  CloudUpload,
  DeleteOutline,
  Schedule,
  People
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { eventService } from '../../services/eventService';
import EventParticipationManagement from './EventParticipationManagement';

const EventManagement = () => {
  const [openDialog, setOpenDialog] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    event_date: '',
    start_time: '',
    end_time: '',
    is_active: true,
    participation_open: true,
    available_slots: []
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [bannerDialog, setBannerDialog] = useState({ open: false, event: null });
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [timeSlot, setTimeSlot] = useState('');
  const [participationDialog, setParticipationDialog] = useState({ open: false, event: null });
  const queryClient = useQueryClient();

  // Buscar eventos
  const { data: eventsData, isLoading } = useQuery({
    queryKey: ['events'],
    queryFn: () => eventService.getAllEvents(),
  });

  const events = eventsData?.data?.events || [];

  // Mutations
  const createEventMutation = useMutation({
    mutationFn: (data) => eventService.createEvent(data),
    onSuccess: () => {
      queryClient.invalidateQueries(['events']);
      setSnackbar({ open: true, message: 'Evento criado com sucesso!', severity: 'success' });
      handleCloseDialog();
    },
    onError: (error) => {
      setSnackbar({ open: true, message: error.response?.data?.error || 'Erro ao criar evento', severity: 'error' });
    }
  });

  const updateEventMutation = useMutation({
    mutationFn: ({ id, data }) => eventService.updateEvent(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['events']);
      setSnackbar({ open: true, message: 'Evento atualizado com sucesso!', severity: 'success' });
      handleCloseDialog();
    },
    onError: (error) => {
      setSnackbar({ open: true, message: error.response?.data?.error || 'Erro ao atualizar evento', severity: 'error' });
    }
  });

  const deleteEventMutation = useMutation({
    mutationFn: (id) => eventService.deleteEvent(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['events']);
      setSnackbar({ open: true, message: 'Evento deletado com sucesso!', severity: 'success' });
    },
    onError: (error) => {
      setSnackbar({ open: true, message: error.response?.data?.error || 'Erro ao deletar evento', severity: 'error' });
    }
  });

  const uploadBannerMutation = useMutation({
    mutationFn: ({ eventId, formData }) => eventService.uploadBanner(eventId, formData),
    onSuccess: () => {
      queryClient.invalidateQueries(['events']);
      setSnackbar({ open: true, message: 'Banner atualizado com sucesso!', severity: 'success' });
      handleCloseBannerDialog();
    },
    onError: (error) => {
      setSnackbar({ open: true, message: error.response?.data?.error || 'Erro ao atualizar banner', severity: 'error' });
    }
  });

  const removeBannerMutation = useMutation({
    mutationFn: (eventId) => eventService.removeBanner(eventId),
    onSuccess: () => {
      queryClient.invalidateQueries(['events']);
      setSnackbar({ open: true, message: 'Banner removido com sucesso!', severity: 'success' });
      handleCloseBannerDialog();
    },
    onError: (error) => {
      setSnackbar({ open: true, message: error.response?.data?.error || 'Erro ao remover banner', severity: 'error' });
    }
  });

  const handleOpenDialog = (event = null) => {
    if (event) {
      setEditingEvent(event);
      setFormData({
        title: event.title || '',
        description: event.description || '',
        event_date: event.event_date ? event.event_date.split('T')[0] : '',
        start_time: event.start_time || '',
        end_time: event.end_time || '',
        is_active: event.is_active !== undefined ? event.is_active : true,
        participation_open: event.participation_open !== undefined ? event.participation_open : true,
        available_slots: Array.isArray(event.available_slots) ? event.available_slots : []
      });
    } else {
      setEditingEvent(null);
      setFormData({
        title: '',
        description: '',
        event_date: '',
        start_time: '',
        end_time: '',
        is_active: true,
        participation_open: true,
        available_slots: []
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingEvent(null);
    setFormData({
      title: '',
      description: '',
      event_date: '',
      start_time: '',
      end_time: '',
      is_active: true,
      participation_open: true,
      available_slots: []
    });
  };

  const handleOpenBannerDialog = (event) => {
    setBannerDialog({ open: true, event });
    setSelectedFile(null);
    setPreviewUrl(event.banner_image || null);
  };

  const handleCloseBannerDialog = () => {
    setBannerDialog({ open: false, event: null });
    setSelectedFile(null);
    setPreviewUrl(null);
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
    }
  };

  const handleUploadBanner = () => {
    if (!selectedFile) return;
    
    const formData = new FormData();
    formData.append('banner', selectedFile);
    
    uploadBannerMutation.mutate({ 
      eventId: bannerDialog.event.id, 
      formData 
    });
  };

  const handleRemoveBanner = () => {
    if (window.confirm('Tem certeza que deseja remover o banner deste evento?')) {
      removeBannerMutation.mutate(bannerDialog.event.id);
    }
  };

  const addTimeSlot = () => {
    if (timeSlot && !(formData.available_slots || []).includes(timeSlot)) {
      setFormData({
        ...formData,
        available_slots: [...(formData.available_slots || []), timeSlot]
      });
      setTimeSlot('');
    }
  };

  const removeTimeSlot = (slot) => {
    setFormData({
      ...formData,
      available_slots: (formData.available_slots || []).filter(s => s !== slot)
    });
  };

  const handleSubmit = () => {
    if (editingEvent) {
      updateEventMutation.mutate({ id: editingEvent.id, data: formData });
    } else {
      createEventMutation.mutate(formData);
    }
  };

  const handleDelete = (id) => {
    if (window.confirm('Tem certeza que deseja deletar este evento?')) {
      deleteEventMutation.mutate(id);
    }
  };

  const getStatusColor = (isActive) => {
    return isActive ? 'success' : 'error';
  };

  const getStatusLabel = (isActive) => {
    return isActive ? 'Ativo' : 'Inativo';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const isEventPast = (eventDate) => {
    return new Date(eventDate) < new Date();
  };

  const handleOpenParticipationDialog = (event) => {
    setParticipationDialog({ open: true, event });
  };

  const handleCloseParticipationDialog = () => {
    setParticipationDialog({ open: false, event: null });
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress sx={{ color: '#FF6B35' }} />
      </Box>
    );
  }

  return (
    <Box>
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h5" sx={{ fontFamily: 'Orbitron', fontWeight: 700, color: '#FF6B35' }}>
              Gerenciar Eventos
            </Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleOpenDialog()}
              sx={{
                bgcolor: '#FF6B35',
                '&:hover': { bgcolor: '#FF8A65' }
              }}
            >
              Adicionar Evento
            </Button>
          </Box>

          <TableContainer component={Paper} sx={{ bgcolor: 'rgba(10, 10, 10, 0.5)' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Banner</TableCell>
                  <TableCell>Título</TableCell>
                  <TableCell>Data</TableCell>
                  <TableCell>Horário</TableCell>
                  <TableCell>Participações</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {events.map((event, index) => (
                  <TableRow key={event.id || `event-${index}`}>
                    <TableCell>
                      {event.banner_image ? (
                        <Avatar
                          src={event.banner_image}
                          variant="rounded"
                          sx={{ width: 60, height: 40, cursor: 'pointer' }}
                          onClick={() => handleOpenBannerDialog(event)}
                        >
                          <Image />
                        </Avatar>
                      ) : (
                        <Avatar 
                          variant="rounded" 
                          sx={{ 
                            width: 60, 
                            height: 40, 
                            bgcolor: 'rgba(255, 107, 53, 0.1)',
                            cursor: 'pointer'
                          }}
                          onClick={() => handleOpenBannerDialog(event)}
                        >
                          <Image sx={{ color: '#FF6B35' }} />
                        </Avatar>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {event.title}
                      </Typography>
                      <Typography variant="caption" sx={{ opacity: 0.7 }}>
                        {event.description?.substring(0, 50)}...
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(event.event_date)}
                      </Typography>
                      {isEventPast(event.event_date) && (
                        <Chip label="Passado" size="small" color="error" />
                      )}
                    </TableCell>
                    <TableCell>
                      {event.start_time} - {event.end_time}
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <People sx={{ fontSize: 16, color: '#00D4FF' }} />
                        <Typography variant="body2">
                          {event.participations?.length || 0} participações
                        </Typography>
                      </Box>
                      <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                        {(event.available_slots || []).length} horários disponíveis
                      </Typography>
                      {event.participation_open && (
                        <Chip 
                          label="Inscrições abertas" 
                          size="small" 
                          sx={{ 
                            bgcolor: '#4CAF50', 
                            color: 'white',
                            fontSize: '0.7rem',
                            mt: 0.5
                          }} 
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getStatusLabel(event.is_active)}
                        color={getStatusColor(event.is_active)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Box display="flex" gap={1}>
                        <Tooltip title="Editar Evento">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenDialog(event)}
                            sx={{ color: '#00D4FF' }}
                          >
                            <Edit />
                          </IconButton>
                        </Tooltip>
                        
                        <Tooltip title="Editar Banner">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenBannerDialog(event)}
                            sx={{ color: '#FF6B35' }}
                          >
                            <Image />
                          </IconButton>
                        </Tooltip>
                        
                        <Tooltip title="Gerenciar Participações">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenParticipationDialog(event)}
                            sx={{ color: '#4CAF50' }}
                          >
                            <People />
                          </IconButton>
                        </Tooltip>
                        
                        <Tooltip title="Deletar">
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(event.id)}
                            sx={{ color: '#FF1744' }}
                          >
                            <Delete />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Dialog para Editar/Criar Evento */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingEvent ? 'Editar Evento' : 'Adicionar Evento'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Título do Evento"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              margin="normal"
            />
            <TextField
              fullWidth
              label="Descrição"
              multiline
              rows={4}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              margin="normal"
            />
            <TextField
              fullWidth
              label="Data do Evento"
              type="date"
              value={formData.event_date}
              onChange={(e) => setFormData({ ...formData, event_date: e.target.value })}
              InputLabelProps={{ shrink: true }}
              margin="normal"
            />
            <Box display="flex" gap={2} margin="normal">
              <TextField
                label="Horário de Início"
                type="time"
                value={formData.start_time}
                onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                InputLabelProps={{ shrink: true }}
                sx={{ flex: 1 }}
              />
              <TextField
                label="Horário de Fim"
                type="time"
                value={formData.end_time}
                onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                InputLabelProps={{ shrink: true }}
                sx={{ flex: 1 }}
              />
            </Box>
            <Box sx={{ mt: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  />
                }
                label="Evento Ativo"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.participation_open}
                    onChange={(e) => setFormData({ ...formData, participation_open: e.target.checked })}
                  />
                }
                label="Inscrições Abertas"
                sx={{ ml: 2 }}
              />
            </Box>

            {/* Gerenciamento de Horários */}
            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, color: '#FF6B35' }}>
                Horários Disponíveis para Participação
              </Typography>
              
              <Box display="flex" gap={2} alignItems="center" mb={2}>
                <TextField
                  label="Adicionar Horário"
                  type="time"
                  value={timeSlot}
                  onChange={(e) => setTimeSlot(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ flex: 1 }}
                />
                <Button
                  variant="outlined"
                  onClick={addTimeSlot}
                  disabled={!timeSlot}
                  sx={{
                    borderColor: '#FF6B35',
                    color: '#FF6B35',
                    '&:hover': {
                      borderColor: '#FF8A65',
                      bgcolor: 'rgba(255, 107, 53, 0.05)'
                    }
                  }}
                >
                  Adicionar
                </Button>
              </Box>

              <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
                {(formData.available_slots || []).map((slot, index) => (
                  <Chip
                    key={index}
                    label={slot}
                    onDelete={() => removeTimeSlot(slot)}
                    sx={{
                      m: 0.5,
                      bgcolor: 'rgba(0, 212, 255, 0.1)',
                      color: '#00D4FF',
                      '& .MuiChip-deleteIcon': {
                        color: '#00D4FF'
                      }
                    }}
                  />
                ))}
                {(formData.available_slots || []).length === 0 && (
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.5)', fontStyle: 'italic' }}>
                    Nenhum horário adicionado
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={createEventMutation.isPending || updateEventMutation.isPending}
            sx={{ bgcolor: '#FF6B35', '&:hover': { bgcolor: '#FF8A65' } }}
          >
            {(createEventMutation.isPending || updateEventMutation.isPending) ? 
              <CircularProgress size={20} /> : 'Salvar'
            }
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog para Editar Banner do Evento */}
      <Dialog open={bannerDialog.open} onClose={handleCloseBannerDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          Editar Banner - {bannerDialog.event?.title}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {/* Preview da imagem atual */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, color: '#FF6B35' }}>
                Banner Atual
              </Typography>
              {previewUrl ? (
                <Box sx={{ textAlign: 'center' }}>
                  <img
                    src={previewUrl}
                    alt="Preview do banner"
                    style={{
                      maxWidth: '100%',
                      maxHeight: '200px',
                      borderRadius: '8px',
                      border: '2px solid #FF6B35'
                    }}
                  />
                </Box>
              ) : (
                <Box sx={{ 
                  textAlign: 'center', 
                  p: 4, 
                  border: '2px dashed #FF6B35', 
                  borderRadius: '8px',
                  bgcolor: 'rgba(255, 107, 53, 0.05)'
                }}>
                  <Image sx={{ fontSize: 48, color: '#FF6B35', mb: 1 }} />
                  <Typography variant="body2" sx={{ color: '#FF6B35' }}>
                    Nenhum banner definido
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Upload de nova imagem */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, color: '#FF6B35' }}>
                Nova Imagem
              </Typography>
              <input
                accept="image/*"
                style={{ display: 'none' }}
                id="event-banner-upload"
                type="file"
                onChange={handleFileSelect}
              />
              <label htmlFor="event-banner-upload">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<CloudUpload />}
                  sx={{
                    borderColor: '#FF6B35',
                    color: '#FF6B35',
                    '&:hover': {
                      borderColor: '#FF8A65',
                      color: '#FF8A65',
                      bgcolor: 'rgba(255, 107, 53, 0.05)'
                    }
                  }}
                >
                  Selecionar Imagem
                </Button>
              </label>
              {selectedFile && (
                <Typography variant="body2" sx={{ mt: 1, color: '#00D4FF' }}>
                  Arquivo selecionado: {selectedFile.name}
                </Typography>
              )}
            </Box>

            {/* Informações sobre o arquivo */}
            <Box sx={{ 
              p: 2, 
              bgcolor: 'rgba(0, 212, 255, 0.05)', 
              borderRadius: '8px',
              border: '1px solid rgba(0, 212, 255, 0.2)'
            }}>
              <Typography variant="body2" sx={{ color: '#00D4FF', fontWeight: 600, mb: 1 }}>
                Informações:
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', color: '#00D4FF' }}>
                • Formatos aceitos: JPEG, JPG, PNG
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', color: '#00D4FF' }}>
                • Tamanho máximo: 5MB
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', color: '#00D4FF' }}>
                • Dimensões recomendadas: 468x60px
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseBannerDialog}>
            Cancelar
          </Button>
          {bannerDialog.event?.banner_image && (
            <Button
              onClick={handleRemoveBanner}
              variant="outlined"
              startIcon={<DeleteOutline />}
              disabled={removeBannerMutation.isPending}
              sx={{
                borderColor: '#FF1744',
                color: '#FF1744',
                '&:hover': {
                  borderColor: '#FF1744',
                  color: '#FF1744',
                  bgcolor: 'rgba(255, 23, 68, 0.05)'
                }
              }}
            >
              {removeBannerMutation.isPending ? <CircularProgress size={20} /> : 'Remover Banner'}
            </Button>
          )}
          <Button
            onClick={handleUploadBanner}
            variant="contained"
            disabled={!selectedFile || uploadBannerMutation.isPending}
            sx={{ 
              bgcolor: '#FF6B35', 
              '&:hover': { bgcolor: '#FF8A65' },
              '&:disabled': { bgcolor: 'rgba(255, 107, 53, 0.3)' }
            }}
          >
            {uploadBannerMutation.isPending ? <CircularProgress size={20} /> : 'Atualizar Banner'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog para Gerenciar Participações */}
      <Dialog 
        open={participationDialog.open} 
        onClose={handleCloseParticipationDialog} 
        maxWidth="lg" 
        fullWidth
        fullScreen
      >
        <DialogContent sx={{ p: 0 }}>
          {participationDialog.event && (
            <EventParticipationManagement 
              eventId={participationDialog.event.id}
              onClose={handleCloseParticipationDialog}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Snackbar para feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EventManagement;
